/**
 * Rewrites every same-origin proposal-media URL found anywhere in a value
 * (recursively) to carry a share token as a `?share=` query param.
 *
 * Why: the server's media route (GET /api/proposals/:id/media/:file) now
 * requires a credential — a session, a render-token cookie, or a share
 * token (server/index.ts's /api gate). `/share/:token`'s own JSON fetch
 * already carries the token as a header (see setShareToken in api/client.ts),
 * but an `<img src>` can't set a header, so the URL itself has to carry it —
 * accepted ONLY on the media route, never as a general credential.
 *
 * Media URLs are same-origin strings shaped exactly like
 * "/api/proposals/<id>/media/<file>" (see src/domain/api.ts) — walking the
 * whole tree for that shape is more robust than tracking every specific
 * field a document might put one in.
 */
const MEDIA_URL_RE = /^\/api\/proposals\/[^/]+\/media\/[^/?#]+/;

export function withShareToken<T>(value: T, token: string): T {
  if (typeof value === 'string') {
    if (!MEDIA_URL_RE.test(value)) return value;
    const url = new URL(value, window.location.origin);
    url.searchParams.set('share', token);
    return (url.pathname + url.search) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => withShareToken(item, token)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      out[key] = withShareToken(entry, token);
    }
    return out as T;
  }
  return value;
}
